/**
 * Money primitives.
 *
 * Specification and golden vectors: ../../test/money.spec.ts — read that first.
 * Master plan §2 D1.
 *
 * Rules this file exists to enforce:
 *   - No floating point on any money path, ever. Amounts are bigint minor
 *     units (haléře for CZK, cents for EUR) and carry their currency.
 *   - One rounding implementation, shared by device and server, so a replica
 *     folding the same events reaches a byte-identical total.
 *   - VAT per §37 ZDPH is an exact fraction of the gross, rounded once to the
 *     minor unit. VAT is NOT rounded to whole koruna (that changed in 2019).
 *   - Cash rounding is a property of the TENDER, not of the check, and its
 *     difference sits outside the VAT base.
 */

/** ISO 4217 currency code. Kept as a branded string so it cannot be a bare string. */
export type Currency = string & { readonly __currency: unique symbol };

export const CZK = "CZK" as Currency;
export const EUR = "EUR" as Currency;

/**
 * An amount in minor units, tagged with its currency.
 *
 * `minor` is `bigint` and never `number`: JavaScript numbers lose integer
 * precision past 2^53 and, more importantly, invite arithmetic that silently
 * produces fractions of a haléř.
 */
export interface Money {
  readonly minor: bigint;
  readonly currency: Currency;
}

export function money(minor: bigint, currency: Currency): Money {
  return { minor, currency };
}

export const czk = (minor: bigint): Money => money(minor, CZK);

function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) {
    throw new Error(
      `refusing to mix currency ${a.currency} with ${b.currency} — convert explicitly and store both amounts`,
    );
  }
}

export function addMoney(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.minor + b.minor, a.currency);
}

export function subMoney(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.minor - b.minor, a.currency);
}

export function sumMoney(amounts: readonly Money[], currency: Currency): Money {
  let total = 0n;
  for (const a of amounts) {
    if (a.currency !== currency) {
      throw new Error(`refusing to mix currency ${a.currency} with ${currency}`);
    }
    total += a.minor;
  }
  return money(total, currency);
}

/**
 * Integer division rounding half AWAY FROM ZERO.
 *
 * The symmetry is deliberate and load-bearing: a refund must reverse a sale
 * exactly. Rounding half-up (toward +infinity) or truncating would make
 * `refund(x) !== -sale(x)` for half-values, which shows up as one-haléř drift
 * in the VAT recap months later.
 */
export function divRound(numerator: bigint, denominator: bigint): bigint {
  if (denominator <= 0n) {
    throw new Error("divRound: denominator must be positive");
  }
  const negative = numerator < 0n;
  const abs = negative ? -numerator : numerator;
  const quotient = (abs * 2n + denominator) / (denominator * 2n);
  return negative ? -quotient : quotient;
}

/**
 * VAT rate in basis points: 21 % is 2100, 12 % is 1200.
 *
 * Basis points rather than whole percent because several EU jurisdictions we
 * intend to expand into use fractional rates, and retrofitting the unit later
 * would touch every stored rate.
 */
export type RateBp = number;

/**
 * VAT contained in a gross amount — §37 ZDPH.
 *
 *   vat = gross × r / (100 + r)
 *
 * computed as an exact integer fraction and rounded once. Note this is applied
 * to a per-rate gross SUM on a receipt, never summed per line: see the
 * "VAT is computed per rate bucket" vectors in the spec.
 */
export function vatFromGross(grossMinor: bigint, rateBp: RateBp): bigint {
  const r = BigInt(rateBp);
  if (r === 0n) return 0n;
  return divRound(grossMinor * r, 10_000n + r);
}

/** Net = gross − contained VAT. */
export function netFromGross(grossMinor: bigint, rateBp: RateBp): bigint {
  return grossMinor - vatFromGross(grossMinor, rateBp);
}

/** VAT added on top of a net amount: net × r / 100. */
export function vatFromNet(netMinor: bigint, rateBp: RateBp): bigint {
  const r = BigInt(rateBp);
  if (r === 0n) return 0n;
  return divRound(netMinor * r, 10_000n);
}

/** A unit price and the rate it was resolved at. Enough to scale a line. */
export interface ScalableLine {
  readonly grossMinor: bigint;
  readonly vatMinor: bigint;
  readonly netMinor: bigint;
  readonly rateBp: RateBp;
}

/**
 * A priced line at a quantity — three halves of a Pilsner, half a portion.
 *
 * `quantityMilli` is thousandths: 1000 is one, 1500 is one and a half.
 *
 * VAT IS RECOMPUTED FROM THE SCALED GROSS rather than scaled directly, so a
 * half portion rounds once instead of inheriting half of an already-rounded
 * haléř. Scaling the VAT would drift a line at a time, in a way that sums
 * correctly on the receipt and not in the VAT return — the hardest class of bug
 * to find and the least excusable to ship.
 *
 * IT LIVES HERE BECAUSE THERE ARE NOW TWO WRITERS. This was a private function
 * in the till's session, correct and tested, and the guest's phone became the
 * second thing able to put a line on a check. A second copy of a rounding rule
 * is two rules, and the one that drifts is the one nobody is looking at.
 */
export function scaleLine<T extends ScalableLine>(line: T, quantityMilli: number): T {
  if (quantityMilli === 1000) return line;
  const grossMinor = divRound(line.grossMinor * BigInt(quantityMilli), 1000n);
  const vatMinor = vatFromGross(grossMinor, line.rateBp);
  return { ...line, grossMinor, vatMinor, netMinor: grossMinor - vatMinor };
}

/**
 * Czech cash rounding.
 *
 * Only the cash-payable total rounds, and only to whole koruna. The returned
 * `difference` is booked as a non-taxable rounding line — it is NOT part of the
 * tax base, and it must be reversed if the tender changes (a card payment on
 * the same check does not round).
 *
 * Callers must treat this as a function of the tender, computed at tender time.
 */
export function roundCashCzk(totalMinor: bigint): {
  rounded: bigint;
  difference: bigint;
} {
  const rounded = divRound(totalMinor, 100n) * 100n;
  return { rounded, difference: rounded - totalMinor };
}

export interface AllocationBucket {
  readonly key: string;
  readonly weight: bigint;
}

/**
 * Distribute `amount` across weighted buckets by largest remainder, so that the
 * parts sum EXACTLY to the input with no unit created or destroyed.
 *
 * Two uses, both of which must be exact:
 *   1. Apportioning a combo price across its components so each keeps its own
 *      VAT rate. A menu containing food (12 %) and beer (21 %) that does not
 *      apportion is a tax error on every combo sold.
 *   2. Distributing an order-scoped discount across VAT rate buckets.
 *
 * Ties are broken by key order so two replicas folding the same events produce
 * identical output. That determinism is what lets the device and the server
 * agree without a reconciliation step.
 */
export function allocateProportionally(
  amount: bigint,
  buckets: readonly AllocationBucket[],
): Record<string, bigint> {
  const out: Record<string, bigint> = {};
  if (buckets.length === 0) return out;

  const totalWeight = buckets.reduce((acc, b) => acc + b.weight, 0n);
  if (totalWeight === 0n) {
    for (const b of buckets) out[b.key] = 0n;
    return out;
  }

  const remainders: { key: string; remainder: bigint }[] = [];
  let assigned = 0n;

  for (const b of buckets) {
    const exact = amount * b.weight;
    // Truncating division; the shortfall is redistributed below.
    const share = exact / totalWeight;
    out[b.key] = share;
    assigned += share;
    remainders.push({ key: b.key, remainder: exact - share * totalWeight });
  }

  let leftover = amount - assigned;
  if (leftover === 0n) return out;

  // Largest remainder first; key order breaks ties deterministically.
  remainders.sort((a, b) => {
    if (a.remainder !== b.remainder) return a.remainder > b.remainder ? -1 : 1;
    return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
  });

  const step = leftover > 0n ? 1n : -1n;
  let i = 0;
  while (leftover !== 0n) {
    const bucket = remainders[i % remainders.length]!;
    out[bucket.key] = (out[bucket.key] ?? 0n) + step;
    leftover -= step;
    i++;
  }

  return out;
}

/**
 * JSON boundary. Journal payloads carry money as decimal strings because JSON
 * has no integer type that survives a bigint, and because a string round-trips
 * through every language a partner might integrate from.
 */
export const minorToJson = (minor: bigint): string => minor.toString();

export function minorFromJson(value: string): bigint {
  if (!/^-?\d+$/.test(value)) {
    throw new Error(`minorFromJson: expected an integer string, got ${JSON.stringify(value)}`);
  }
  return BigInt(value);
}

/**
 * Display only. Never feed the result of this back into arithmetic.
 */
export function formatMoney(amount: Money, locale = "cs-CZ"): string {
  const major = Number(amount.minor) / 100;
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: amount.currency,
  }).format(major);
}
